/**
 * Финансы.
 *
 * У админа — балансы команды и кнопка «отметить выплату». Система не
 * переводит деньги: ты платишь вручную, как удобно, а здесь фиксируется
 * факт. Поэтому реквизитов нигде нет.
 *
 * У исполнителя — свой баланс и история операций, где видно, за что
 * именно начислено и что вычтено.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, Person, Row, toast } from '../components/ui';
import { dateTime, money, moneySigned, plural } from '../lib/format';
import type { LedgerEntry } from '../lib/types';

type Balances = {
  totalToPay: number;
  /** Сумма всех выплат за всё время, в копейках */
  paidTotal: number;
  paidCount: number;
  paidSince: string | null;
  users: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    roles: string[];
    balance: number;
    note: string | null;
  }>;
};

type FinanceMe = {
  balance: number;
  month: { earned: number; penalties: number; paidOut: number };
  history: LedgerEntry[];
};

export function FinancePage() {
  const { can } = useAuth();
  return can('finance.view.all') ? <AdminFinance /> : <MyFinance />;
}

function AdminFinance() {
  const qc = useQueryClient();
  const [paying, setPaying] = useState<string | null>(null);
  const [bonusTo, setBonusTo] = useState<string | null>(null);
  const [bonusAmount, setBonusAmount] = useState(0);
  const [bonusReason, setBonusReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['balances'],
    queryFn: () => api.get<Balances>('/api/finance/balances'),
  });

  const payout = useMutation({
    mutationFn: (userId: string) => api.post('/api/finance/payouts', { userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['payouts'] });
      toast('Выплата отмечена');
      setPaying(null);
    },
    onError: (e: Error) => {
      toast(e.message, '⚠');
      setPaying(null);
    },
  });

  const { data: payouts } = useQuery({
    queryKey: ['payouts'],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          amount: number;
          createdAt: string;
          confirmedAt: string | null;
          user: { id: string; displayName: string; avatarUrl: string | null };
        }>
      >('/api/finance/payouts'),
  });

  /**
   * Премия и удержание — одна и та же запись, отличается только знаком.
   * Отдельной «отмены премии» нет и не нужно: леджер только дополняется,
   * ошибка исправляется встречной записью, а не правкой старой.
   */
  const bonus = useMutation({
    mutationFn: (v: { userId: string; amount: number; reason: string }) =>
      api.post('/api/finance/adjust', { ...v, isBonus: v.amount > 0 }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['finance-me'] });
      setBonusTo(null);
      toast(v.amount > 0 ? 'Премия начислена' : 'Удержание записано');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const toPay = data?.users.filter((u) => u.balance > 0) ?? [];

  return (
    <>
      <PageHeader title="Финансы" sub="Начисления, штрафы и выплаты" />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="sec !mt-0">Балансы команды</div>
            {isLoading ? (
              <Loading rows={4} />
            ) : (
              <div className="card p-0 overflow-hidden">
                {data?.users.map((u) => (
                  <div key={u.id} className="border-b border-[#1A1A24] last:border-0">
                    <div className="flex items-center gap-4 px-4 py-3.5 flex-wrap">
                    <Person
                      id={u.id}
                      name={u.displayName}
                      url={u.avatarUrl}
                      sub={u.note ?? undefined}
                      size={32}
                    />
                    <div className="flex-1" />
                    <span
                      className={`mono font-extrabold text-[15px] ${
                        u.balance < 0 ? 'text-dang' : ''
                      }`}
                    >
                      {money(u.balance)}
                    </span>
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        setBonusTo(bonusTo === u.id ? null : u.id);
                        setBonusAmount(0);
                        setBonusReason('');
                      }}
                      title="Премия или ручная корректировка"
                    >
                      ＋ Премия
                    </button>
                    {u.balance > 0 && (
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          setPaying(u.id);
                          payout.mutate(u.id);
                        }}
                        disabled={paying === u.id}
                      >
                        {paying === u.id ? '…' : 'Отметить выплату'}
                      </button>
                    )}
                    </div>
                    {bonusTo === u.id && (
                      <div className="px-4 pb-4 -mt-1">
                        <div className="bg-bg rounded-lg p-3 space-y-2">
                          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2">
                            <div>
                              <div className="label">Сумма, ₽</div>
                              <input
                                className="input mono"
                                type="number"
                                value={bonusAmount}
                                onChange={(e) => setBonusAmount(Number(e.target.value))}
                                autoFocus
                              />
                            </div>
                            <div>
                              <div className="label">За что</div>
                              <input
                                className="input"
                                value={bonusReason}
                                maxLength={500}
                                onChange={(e) => setBonusReason(e.target.value)}
                                placeholder="Вытащил съёмку в выходной"
                              />
                            </div>
                          </div>
                          <div className="text-[11.5px] text-muted2 leading-relaxed">
                            Отрицательная сумма — это удержание, а не премия.
                            Запись попадёт в историю человека и в журнал,
                            стереть её потом нельзя, только исправить встречной.
                          </div>
                          <div className="flex gap-2">
                            <button className="btn btn-sm flex-1" onClick={() => setBonusTo(null)}>
                              Отмена
                            </button>
                            <button
                              className="btn btn-sm btn-primary flex-1"
                              disabled={!bonusAmount || !bonusReason.trim() || bonus.isPending}
                              onClick={() =>
                                bonus.mutate({
                                  userId: u.id,
                                  amount: bonusAmount,
                                  reason: bonusReason.trim(),
                                })
                              }
                            >
                              {bonusAmount < 0 ? 'Удержать' : 'Начислить премию'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="sec">История выплат</div>
            {payouts?.length ? (
              <div className="card p-0 overflow-hidden">
                {payouts.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-4 px-4 py-3 border-b border-[#1A1A24] last:border-0"
                  >
                    <Person id={p.user.id} name={p.user.displayName} url={p.user.avatarUrl} size={26} />
                    <div className="flex-1" />
                    <span className="text-xs text-muted2">{dateTime(p.createdAt)}</span>
                    <span
                      className={`text-[11px] ${p.confirmedAt ? 'text-ok' : 'text-muted2'}`}
                      title={p.confirmedAt ? 'Получение подтверждено' : 'Ожидает подтверждения'}
                    >
                      {p.confirmedAt ? '✓ получено' : 'ждёт подтверждения'}
                    </span>
                    <span className="mono font-bold w-24 text-right">{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card">
                <Empty title="Выплат пока не было" />
              </div>
            )}
          </div>

          <div>
            <div className="relative overflow-hidden rounded-2xl border border-[#3B2E70] p-6 bg-gradient-to-br from-[#241948] via-[#181231] to-[#13131C]">
              <div className="absolute w-[340px] h-[340px] -right-32 -top-44 rounded-full bg-[radial-gradient(circle,#7C5CFF33,transparent_70%)]" />
              <div className="relative">
                <div className="text-[10.5px] tracking-[0.17em] text-accent2 font-bold">
                  К ВЫПЛАТЕ
                </div>
                <div className="text-4xl font-extrabold tracking-[-0.035em] mt-2.5 mb-1.5 mono">
                  {money(data?.totalToPay ?? 0)}
                </div>
                <div className="text-[13.5px] text-[#B4AFD0]">
                  {toPay.length} {toPay.length === 1 ? 'человек ждёт' : 'человека ждут'}
                </div>
              </div>
            </div>

            <div className="text-[11.5px] text-muted2 mt-3.5 px-1 leading-relaxed">
              Деньги переводишь сам, как удобно. Система только считает суммы и фиксирует
              факт выплаты — реквизиты нигде не хранятся.
            </div>

            {/* Сколько уже отдано. Стоит рядом с «к выплате» намеренно:
                вместе они отвечают на вопрос «сколько стоила работа»,
                поодиночке — только на половину */}
            <div className="sec">Выплачено за всё время</div>
            <div className="card">
              <div className="mono text-2xl font-extrabold">{money(data?.paidTotal ?? 0)}</div>
              <div className="text-[11.5px] text-muted2 mt-1.5 leading-relaxed">
                {data?.paidCount
                  ? `${data.paidCount} ${plural(data.paidCount, 'выплата', 'выплаты', 'выплат')}${
                      data.paidSince
                        ? ` с ${new Date(data.paidSince).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}`
                        : ''
                    }`
                  : 'выплат пока не было'}
              </div>
              {data?.paidTotal ? (
                <div className="text-[11.5px] text-muted2 mt-2.5 pt-2.5 border-t border-[#1A1A24] leading-relaxed">
                  Считается по журналу начислений, а не по кнопке «отметить»:
                  отменённые записи сюда не попадают.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function MyFinance() {
  const { data, isLoading } = useQuery({
    queryKey: ['finance-me'],
    queryFn: () => api.get<FinanceMe>('/api/finance/me'),
  });

  return (
    <>
      <PageHeader title="Финансы" />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="sec !mt-0">История операций</div>
            {isLoading ? (
              <Loading rows={5} />
            ) : !data?.history.length ? (
              <div className="card">
                <Empty title="Операций пока нет" hint="Начисления появятся после первой приёмки" />
              </div>
            ) : (
              <div className="card p-0 overflow-hidden">
                {data.history.map((op) => (
                  <div
                    key={op.id}
                    className="flex items-center gap-4 px-4 py-3.5 border-b border-[#1A1A24] last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{op.reason ?? typeLabel(op.type)}</div>
                      <div className="text-[11.5px] text-muted2">{dateTime(op.createdAt)}</div>
                    </div>
                    <span
                      className={`mono font-bold ${op.amount > 0 ? 'text-ok' : 'text-dang'}`}
                    >
                      {moneySigned(op.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="relative overflow-hidden rounded-2xl border border-[#3B2E70] p-6 bg-gradient-to-br from-[#241948] via-[#181231] to-[#13131C]">
              <div className="absolute w-[340px] h-[340px] -right-32 -top-44 rounded-full bg-[radial-gradient(circle,#7C5CFF33,transparent_70%)]" />
              <div className="relative">
                <div className="text-[10.5px] tracking-[0.17em] text-accent2 font-bold">БАЛАНС</div>
                <div className="text-4xl font-extrabold tracking-[-0.035em] mt-2.5 mb-1.5 mono">
                  {money(data?.balance ?? 0)}
                </div>
                <div className="text-[13.5px] text-[#B4AFD0]">Выплата раз в неделю</div>
              </div>
            </div>

            <div className="sec">За месяц</div>
            <div className="card">
              <Row label="Заработано">
                <span className="mono text-ok">{money(data?.month.earned ?? 0)}</span>
              </Row>
              <Row label="Штрафы">
                <span className="mono text-dang">{money(data?.month.penalties ?? 0)}</span>
              </Row>
              <Row label="Выплачено">
                <span className="mono">{money(data?.month.paidOut ?? 0)}</span>
              </Row>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

const typeLabel = (t: LedgerEntry['type']) =>
  ({
    ACCRUAL_SHOOT: 'Съёмка',
    ACCRUAL_EDIT: 'Монтаж',
    PENALTY: 'Штраф',
    PAYOUT: 'Выплата',
    ADJUSTMENT: 'Корректировка',
    BONUS: 'Премия',
  })[t];
